import {
  ClusterGroupsResponse,
  ClusterGroupsUpdateResponse,
  DetectionCollectionUpdateResponse,
  // FetchDownloadAssetResponse,
  FetchRedactTDOResponse,
  ProgressResponse,
  TranscriptionResponse,
  WorkerReadyResponse,
  DataFetchedForDetectionType,
  WorkerWarnFailurePayload,
  LastSaveMediaDataDisplayPayload,
  ClusterMapResponse,
  PollDownloadAssetStop,
  DetectionSeriesLengthResponse,
} from '../models';
import { BoundingPolyTree } from '@common/web-worker';
import {
  UDRsPolyAsset,
  HighlightedOverlay,
} from '@common-modules/mediaDetails/models';
import { createAction } from '@reduxjs/toolkit';
import { TDOId } from '@common/state/modules/universal/models/Brands';
import { SetSelectedGroupsRequest } from '@common-modules/mediaDetails';
import {
  AuditLogEventPayload,
  LOG_AUDIT_EVENT,
} from '@common-modules/mediaDetails/actions';
import { ArrayOrSingle } from 'ts-essentials';
import { PollToDownloadResponse } from '../services';
import { META_FLAG as RETURN_META_FLAG } from 'src/utils/workeredux/worker/actions';
import { BackgroundBlurSlice } from '@common/state/modules/mediaDetails/store.models';

export const WORKER_ID = '792209cb-d64f-43af-becc-b1a67a3747cf';

/**
 * Outgoing
 */
export const SET_LAST_SAVE_MEDIA_DATA_DISPLAY =
  createReturnAction<LastSaveMediaDataDisplayPayload>(
    'vtn-redact-web-worker/SET_LAST_SAVE_MEDIA_DATA_DISPLAY'
  );

export const OUT_HAS_SAVED_UDRS = createReturnAction<{
  hasSavedUdrs: boolean | undefined;
}>('vtn-redact-web-worker/OUT_HAS_SAVED_UDRS');

export const SET_SAVE_MEDIA_DATA_MAX_ATTEMPT_FAILURE = createReturnAction<void>(
  'vtn-redact-web-worker/SET_SAVE_MEDIA_DATA_MAX_ATTEMPT_FAILURE'
);
export const SET_SAVE_TRANSCRIPT_MAX_ATTEMPT_FAILURE = createReturnAction<void>(
  'vtn-redact-web-worker/SET_SAVE_TRANSCRIPT_MAX_ATTEMPT_FAILURE'
);

export const SET_SAVE_IS_RUNNING = createReturnAction<{
  isSaveRunning: boolean;
}>('vtn-redact-web-worker/SET_SAVE_IS_RUNNING');

export const WORKER_WARN_FAILURE = createReturnAction<WorkerWarnFailurePayload>(
  'vtn-redact-web-worker/WORKER_WARN_FAILURE'
);

export const OUT_WORKER_READY = createReturnAction<WorkerReadyResponse>(
  'vtn-redact-web-worker/OUT_WORKER_READY'
);

export const OUT_CLUSTER_MAP = createReturnAction<ClusterMapResponse>(
  'vtn-redact-web-worker/OUT_CLUSTER_MAP'
);

export const OUT_UPDATED_CLUSTER_ASSIGNMENT = createReturnAction<{
  clusterId: string;
  clusterMap: ClusterMapResponse;
}>('vtn-redact-web-worker/OUT_UPDATED_CLUSTER_ASSIGNMENT');

export const OUT_DETECTION_CLUSTER_GROUPS =
  createReturnAction<ClusterGroupsResponse>(
    'vtn-redact-web-worker/OUT_DETECTION_CLUSTER_GROUPS'
  );

export const OUT_DETECTION_CLUSTER_GROUPS_UPDATE =
  createReturnAction<ClusterGroupsUpdateResponse>(
    'vtn-redact-web-worker/OUT_DETECTION_CLUSTER_GROUPS_UPDATE'
  );

export const OUT_UDR_CLUSTER_GROUPS = createReturnAction<ClusterGroupsResponse>(
  'vtn-redact-web-worker/OUT_UDR_CLUSTER_GROUPS'
);

export const OUT_UDR_CLUSTER_GROUPS_UPDATE =
  createReturnAction<ClusterGroupsUpdateResponse>(
    'vtn-redact-web-worker/OUT_UDR_CLUSTER_GROUPS_UPDATE'
  );

export const OUT_PROGRESS = createReturnAction<ProgressResponse>(
  'vtn-redact-web-worker/OUT_PROGRESS'
);

export const OUT_BOUNDING_POLY_COLLECTION = createReturnAction<{
  [key: string]: BoundingPolyTree | undefined;
}>('vtn-redact-web-worker/OUT_BOUNDING_POLY_COLLECTION');

export const OUT_DETECTION_COLLECTION_UPDATE =
  createReturnAction<DetectionCollectionUpdateResponse>(
    'vtn-redact-web-worker/OUT_DETECTION_COLLECTION_UPDATE'
  );

export const OUT_UDR_COLLECTION = createReturnAction<
  BoundingPolyTree | undefined
>('vtn-redact-web-worker/OUT_UDR_COLLECTION');

export const OUT_TEXT_OVERLAY_COLLECTION = createReturnAction<
  BoundingPolyTree | undefined
>('vtn-redact-web-worker/OUT_TEXT_OVERLAY_COLLECTION');

export const OUT_TRANSCRIPTION_COLLECTION =
  createReturnAction<TranscriptionResponse>(
    'vtn-redact-web-worker/OUT_TRANSCRIPTION_COLLECTION'
  );

/**
 * A transcript fetch failed. Success is reported through OUT_PROGRESS, which a
 * failure deliberately does not touch — the page-load path reads
 * "progress === 1 with no transcript" as "never run". So the audio tab needs
 * this to learn its fetch is over, rather than holding "Transcribing…" until
 * the user navigates away (VE-30784).
 */
export const OUT_TRANSCRIPTION_FETCH_FAILED = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_TRANSCRIPTION_FETCH_FAILED'
);

/**
 * The speaker-separation fetch is over — succeeded, failed, or came back
 * empty. Says nothing about whether it found anything.
 *
 * The audio tab needs this because the two fetches race. The transcript
 * usually wins, and rendering on it alone commits the tab to the flat layout
 * before anyone knows whether there are speaker segments; when they arrive the
 * panel re-lays out under the reader. Unlike the transcript, separation
 * reports no progress of its own (deliberately — `transcription` belongs to
 * the transcript fetch, see onActionInLoadSpeakerSeparationData), so there is
 * nothing else to wait on.
 */
export const OUT_SPEAKER_SEPARATION_FETCH_SETTLED = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_SPEAKER_SEPARATION_FETCH_SETTLED'
);

export const OUT_DATA_FETCHED_FOR_DETECTION_TYPE =
  createReturnAction<DataFetchedForDetectionType>(
    'vtn-redact-web-worker/OUT_DATA_FETCHED_FOR_DETECTION_TYPE'
  );

export const OUT_FETCH_REDACT_TDO_SUCCESS =
  createReturnAction<FetchRedactTDOResponse>(
    'vtn-redact-web-worker/OUT_FETCH_REDACT_TDO_SUCCESS'
  );

export const OUT_SELECTED_BOUNDINGPOLY_GROUP =
  createReturnAction<SetSelectedGroupsRequest>(
    'vtn-redact-web-worker/OUT_SELECTED_BOUNDINGPOLY_GROUP'
  );

export const OUT_UDR_ASSET = createReturnAction<UDRsPolyAsset>(
  'vtn-redact-web-worker/OUT_UDR_ASSET'
);

export const OUT_UDR_ASSET_UPDATE = createReturnAction<UDRsPolyAsset>(
  'vtn-redact-web-worker/OUT_UDR_ASSET_UPDATE'
);

// export const OUT_FETCH_DOWNLOAD_ASSET_SUCCESS =
//   'vtn-redact-web-worker/OUT_FETCH_DOWNLOAD_ASSET_SUCCESS';
// export const actionOutFetchDownloadAssetSuccess = returnActionCreator<
//   FetchDownloadAssetResponse
// >(OUT_FETCH_DOWNLOAD_ASSET_SUCCESS);

export const OUT_POLL_DOWNLOAD_ASSET_SUCCESS = createReturnAction<{
  payload: PollToDownloadResponse;
}>('vtn-redact-web-worker/OUT_POLL_DOWNLOAD_ASSET_SUCCESS');

// TODO: Fix the type declared here -- it creates issues if createAction is used
export const OUT_CREATE_JOB_SUCCESS = createReturnAction<any>(
  'vtn-redact-web-worker/OUT_CREATE_JOB_SUCCESS'
);

export interface CreateJobFailPayload {
  type: string;
  error?: boolean;
  payload: ArrayOrSingle<{ message: string }>;
  meta: {
    variables: {
      id: TDOId;
      tdoName: string;
    };
  };
}

export const OUT_CREATE_JOB_FAIL = createReturnAction<CreateJobFailPayload>(
  'vtn-redact-web-worker/OUT_CREATE_JOB_FAIL'
);

export const OUT_FETCH_FACE_DETECTION_START = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_FETCH_FACE_DETECTION_START'
);

export const OUT_FETCH_FACE_DETECTION_SUCCESS = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_FETCH_FACE_DETECTION_SUCCESS'
);

export const OUT_FETCH_FACE_DETECTION_FAILURE = createReturnAction<{
  invalid?: boolean;
}>('vtn-redact-web-worker/OUT_FETCH_FACE_DETECTION_FAILURE');

export const OUT_FACE_DETECTION_PROCESSING_START = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_FACE_DETECTION_PROCESSING_START'
);

export const OUT_FACE_DETECTION_PROCESSING_STOP = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_FACE_DETECTION_PROCESSING_STOP'
);

export const OUT_FACE_DETECTION_READY = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_FACE_DETECTION_READY'
);

export const OUT_FACE_DETECTION_NOT_READY = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_FACE_DETECTION_NOT_READY'
);

export const OUT_HAS_SAVED_DETECTIONS = createReturnAction<{
  hasSavedDetections: boolean;
}>('vtn-redact-web-worker/OUT_HAS_SAVED_DETECTIONS');

export const OUT_INTERPOLATION_UPDATE = createReturnAction<void>(
  'vtn-redact-web-worker/OUT_INTERPOLATION_UPDATE'
);

export const OUT_POLL_DOWNLOAD_ASSET_FAILED =
  createReturnAction<PollDownloadAssetStop>(
    'vtn-redact-web-worker/OUT_POLL_DOWNLOAD_ASSET_FAILED'
  );

export const OUT_SET_HIGHLIGHTED_OVERLAY =
  createReturnAction<HighlightedOverlay>(
    'vtn-redact-web-worker/OUT_SET_HIGHLIGHTED_OVERLAY'
  );

export const OUT_DETECTION_SERIES_LENGTH =
  createReturnAction<DetectionSeriesLengthResponse>(
    'vtn-redact-web-worker/OUT_DETECTION_SERIES_LENGTH'
  );

export const OUT_SET_VOICE_MASK_MEDIA_URL = createReturnAction<string>(
  'vtn-redact-web-worker/OUT_SET_VOICE_MASK_MEDIA_URL'
);

export const OUT_LOG_AUDIT_EVENT = createReturnAction<AuditLogEventPayload>(
  LOG_AUDIT_EVENT.type
);

export const OUT_BACKGROUND_BLUR_EDITS = createReturnAction<
  BackgroundBlurSlice[]
>('vtn-redact-web-worker/OUT_BACKGROUND_BLUR_EDITS');

// -----------------

/**
 * Custom action creator that includes the return flag at the top level.
 */
function createReturnAction<P>(type: string) {
  const baseAction = createAction<P>(type);

  return Object.assign(
    (payload: P) =>
      Object.assign(baseAction(payload), { [RETURN_META_FLAG]: true }),
    baseAction // Preserve type inference from createAction
  );
}
